import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const isProd = import.meta.env.PROD;

export type LogNamespace =
  | 'auth'
  | 'clients'
  | 'documents'
  | 'signatures'
  | 'jobs'
  | 'organizations'
  | 'services'
  | 'invoices'
  | 'settings'
  | 'time-tracking'
  | 'users'
  | 'core'
  | 'ui'
  | 'misc';

export function logDebug(namespace: LogNamespace, ...args: unknown[]): void {
  if (isProd) return;
  // Use console.debug so it can be filtered separately in dev tools
  // Kept concise to avoid noisy output.
  console.debug(`[${namespace}]`, ...args);
}

export function logError(
  namespace: LogNamespace,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  const baseMessage = `[${namespace}]`;
  
  // Extract error information for better logging
  let errorMessage = 'Unknown error';
  let errorCode: string | undefined;
  let errorDetails: any = undefined;
  
  if (error instanceof Error) {
    errorMessage = error.message || errorMessage;
    errorCode = (error as any).code;
    errorDetails = {
      name: error.name,
      stack: error.stack,
      ...((error as any).details ? { details: (error as any).details } : {}),
    };
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    // Handle Firebase Functions errors and other structured errors
    const errObj = error as any;
    errorMessage = errObj.message || errObj.error?.message || errorMessage;
    errorCode = errObj.code || errObj.error?.code;
    errorDetails = {
      ...errObj,
      ...(errObj.error ? { nestedError: errObj.error } : {}),
    };
  }
  
  if (isProd) {
    // In production, keep logs compact to avoid leaking sensitive details.
    console.error(baseMessage, errorMessage, {
      code: errorCode,
      ...(context || {}),
    });
    return;
  }

  // In development, log full error objects and context for easier debugging.
  console.error(baseMessage, {
    message: errorMessage,
    code: errorCode,
    error: errorDetails || error,
    context: context ?? {},
  });
}
